/**
 * ForceCompleteDialog - Confirmation dialog for force completing a story
 * Story 284: Web UI override of QA→Done status
 *
 * Allows humans to bypass QA evidence requirement with a confirmation dialog.
 * Records the override with user ID and timestamp.
 */

import { useState, useCallback } from 'react';
import { useUpdateStoryStatus } from '../../hooks/useStoryMutations';
import './ForceCompleteDialog.css';

interface ForceCompleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectSlug: string;
  storyExternalId: string;
}

export function ForceCompleteDialog({
  isOpen,
  onClose,
  projectSlug,
  storyExternalId,
}: ForceCompleteDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const updateStatusMutation = useUpdateStoryStatus();

  const handleForceComplete = useCallback(async () => {
    setError(null);
    try {
      await updateStatusMutation.mutateAsync({
        projectSlug,
        storyExternalId,
        status: 'done',
        human_override: true,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete story');
    }
  }, [projectSlug, storyExternalId, updateStatusMutation, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="force-complete-dialog__backdrop" onClick={onClose}>
      <div
        className="force-complete-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="force-complete-title"
      >
        <h2 id="force-complete-title" className="force-complete-dialog__title">
          Force Complete Story?
        </h2>
        <p className="force-complete-dialog__description">
          QA status establishes a discipline with coding agents to submit evidence
          of QA review passing. You can override this and a note will be added
          recording the override.
        </p>
        {error && (
          <div className="force-complete-dialog__error">
            {error}
          </div>
        )}
        <div className="force-complete-dialog__actions">
          <button
            type="button"
            className="force-complete-dialog__button force-complete-dialog__button--cancel"
            onClick={onClose}
            disabled={updateStatusMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="force-complete-dialog__button force-complete-dialog__button--confirm"
            onClick={handleForceComplete}
            disabled={updateStatusMutation.isPending}
          >
            {updateStatusMutation.isPending ? 'Completing...' : 'Mark as Done'}
          </button>
        </div>
      </div>
    </div>
  );
}
